import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import AutocompleteInput from "../../components/AutoCompleteInput";
import { usePutTemplate } from "../../hook/templates";
import { TemplateNavigationLevel, templateNavigationLevels, templateTypes } from "../../models/templates";
import { useAppSelector } from "../../store";
import { fetchTemplatesSummary } from "../../utils/http/templates";

function CreateNew({ closeCreateNew }: { closeCreateNew: Function }) {

  const selectedPath = useAppSelector((state) => state.templates.selectedPath);

  const [selected, setSelected] = useState<Record<TemplateNavigationLevel, string>>(
    Object.fromEntries(templateNavigationLevels.map((key) => [key, ""])) as Record<TemplateNavigationLevel, string>
  );
  const [newType, setNewType] = useState<string>(templateTypes[0]);
  const [options, setOptions] = useState<Record<TemplateNavigationLevel, string[]>>(
    Object.fromEntries(templateNavigationLevels.map((key) => [key, []])) as Record<TemplateNavigationLevel, string[]>
  );
  const [errors, setErrors] = useState<Record<TemplateNavigationLevel, string>>(
    Object.fromEntries(templateNavigationLevels.map((key) => [key, ""])) as Record<TemplateNavigationLevel, string>
  );

  const { data: templatesSummary } = useQuery({
    queryKey: ["templatesSummary"],
    queryFn: ({ signal }) => fetchTemplatesSummary({ signal }),
    staleTime: 2000,
  });

  useEffect(() => {
    if (templatesSummary) {
      const namespaceOptions = Array.from(
        new Set(
          templatesSummary
            .map((template) => (template.customer_specific ? template.navigationPath.namespace : null))
            .filter((ns): ns is string => ns !== null)
        )
      ).sort();

      setOptions({
        ...options,
        namespace: namespaceOptions,
      });

      if (namespaceOptions.includes(selectedPath!.namespace)) {
        setSelected({ ...selectedPath });
      }
    }
  }, [templatesSummary]);

  useEffect(() => {
    if (selected.namespace && templatesSummary) {
      const filteredTemplates = templatesSummary.filter(
        (template) => template.navigationPath.namespace === selected.namespace
      );
      setOptions({
        ...options,
        category: Array.from(new Set(filteredTemplates.map((t) => t.navigationPath.category))).sort(),
      });
    }
  }, [selected.namespace]);

  const handleSelect = (level: TemplateNavigationLevel, value: string) => {
    setErrors({ ...errors, [level]: "" });
    setSelected({ ...selected, [level]: value.trim() });
  };

  const { mutate: createNewTemplate } = usePutTemplate(selected, "draft");

  const createTemplate = async () => {
    if (!templatesSummary) return;

    if (selected.namespace === "") {
      errors.namespace = "Namespace should be selected.";
    } else if (!options.namespace.includes(selected.namespace) && !/^[A-Za-z0-9_]+$/i.test(selected.namespace)) {
      errors.namespace = "Namespace can only contain alphanumeric characters or underscores.";
    }

    if (selected.category === "") {
      errors.category = "Category should be selected.";
    } else if (!options.category.includes(selected.category) && !/^[A-Za-z0-9_]+$/i.test(selected.category)) {
      errors.category = "Category can only contain alphanumeric characters or underscores.";
    }

    // Names should be unique in the namespace, regardless of the category they belong to
    const existingNames = templatesSummary
      .filter((template) => template.navigationPath.namespace === selected.namespace)
      .map((s) => s.navigationPath.name);

    if (selected.name === "") {
      errors.name = "You should provide a name.";
    } else if (!/^[A-Za-z0-9_]+$/i.test(selected.name)) {
      errors.name = "Name can only contain alphanumeric characters or underscores.";
    } else if (existingNames.includes(selected.name)) {
      errors.name = `A ${selected.name} with this name already exists in this namespace.`;
    }

    if (Object.values(errors).every((value) => value == null || value === "")) {
      await createNewTemplate({
        key: selected,
        category: selected.category,
        type: newType,
      });
      closeCreateNew();
    } else {
      setErrors({ ...errors });
    }
  };

  return (
    <Container>
      <Row>
        <Col sm={6} className="ps-0" style={{ lineHeight: "2em" }}>
          <h4 className="rounded p-2 m-0 mb-3" style={{ backgroundColor: "#e4e6f5" }}>
            Creation
          </h4>
          Templates and macros are used throughout our templates. You can create new ones within a custom namespaces and
          category here. There's a certain hierarchy to follow:
          <ul>
            <li className="mt-3">
              <b>Namespaces</b> are the highest level.
              <br />
              You can view namespaces and content <b>created by Colibry</b>. Colibry namespaces have predefined
              templates and macros. We have tested these carefully cross-client. You can create your{" "}
              <b>own namespaces</b>. Your default namespace is your customer name.
              <br />
              <i>Namespaces can only contain alphanumeric characters and underscores.</i>
            </li>
            <li className="mt-3">
              <b>Categories</b> are a sublevel
              <br />
              To have an additional level of subdivision, you can create categories.
            </li>
            <li className="mt-3">
              <b>Names</b>
              <br />
              The name of your template or macro.
            </li>
            <li className="mt-3">
              <b>Type</b>
              <br />
              We have templates and macros:
              <ul>
                <li>
                  <b>Templates</b> can extend other templates. They can be included in other templates as well.
                </li>
                <li>
                  <b>Macros</b> can be used to create reusable snippets of code, they can have parameters that can be
                  used within that specific macro.
                </li>
              </ul>
            </li>
          </ul>
          <br />
          <br />
        </Col>
        <Col sm={6}>
          <Container style={{ marginTop: "60px" }}>
            <Row className="align-items-top mt-3">
              <Col xs={3} className="text-end mt-2">
                Namespace:
              </Col>
              <Col>
                <AutocompleteInput
                  options={options.namespace}
                  onSelect={(value) => handleSelect("namespace", value)}
                  defaultValue={selected.namespace}
                  error={errors.namespace}
                />
              </Col>
            </Row>
            <Row className="align-items-top mt-3">
              <Col xs={3} className="text-end mt-2">
                Category:
              </Col>
              <Col style={{ position: "relative" }}>
                <AutocompleteInput
                  options={options.category}
                  onSelect={(value) => handleSelect("category", value)}
                  defaultValue={selected.category}
                  error={errors.category}
                />
              </Col>
            </Row>
            <Row className="align-items-top mt-3">
              <Col xs={3} className="text-end mt-2">
                Name:
              </Col>
              <Col>
                <Form.Control
                  type="text"
                  id="name"
                  value={selected.name}
                  onChange={(e) => handleSelect("name", e.target.value)}
                  isInvalid={errors.name ? true : false}
                />
                {errors.name && (
                  <Form.Control.Feedback style={{ textAlign: "right" }} type="invalid">
                    {errors.name}
                  </Form.Control.Feedback>
                )}
              </Col>
            </Row>
            <Row className="mt-3">
              <Col xs={3} className="text-end align-self-start">
                Type:
              </Col>
              <Col>
                {templateTypes.map((type) => (
                  <Form.Check
                    className="mb-2"
                    type="radio"
                    name="templateType"
                    key={"type-" + type}
                    id={type}
                    label={type}
                    checked={newType === type}
                    onChange={() => {
                      setNewType(type);
                    }}
                  />
                ))}
              </Col>
            </Row>
          </Container>
          <Container className="my-4 d-flex justify-content-end">
            <Row>
              <Form.Group as={Col} className="p-0 ps-1 pe-1">
                <Button
                  onClick={() => closeCreateNew()}
                  style={{ width: "100%", backgroundColor: "#e4e6f5", border: "none", color: "#212529" }}
                >
                  Cancel
                </Button>
              </Form.Group>
              <Form.Group as={Col} className="p-0 ps-1 pe-1">
                <Button onClick={createTemplate} style={{ width: "100%" }}>
                  Create
                </Button>
              </Form.Group>
            </Row>
          </Container>
        </Col>
      </Row>
    </Container>
  );
}

export default CreateNew;
